// api.ts
import {
  StartLoading,
  StopLoading,
  ShowLogOutError,
} from "@/core/services/AlertService";
import { useAuthStore } from "@/stores/auth";
import type { Router } from "vue-router";
import { downloadBlob } from "@/composables/useFileDownload";
import {
  getStoredAuthToken,
  readAuthData,
  writeAuthData,
} from "@/services/authStorage";
import { log } from "@/logging";
import { readFieldErrors } from "@/services/problemDetail";
import { runtimeConfig } from "@/config/runtime";

// API base URL — resolved at runtime (P3 Tier 2: build-once). See @/config/runtime.
const API_URL = runtimeConfig.apiUrl;

// OVERLAY TEARDOWN. Every function below that takes a `loadingtext` dismisses the
// overlay in a `finally`, never on the success path alone. Until 2026-08-25 the
// `StopLoading()` calls sat inline after `await fetch(...)`, which covered an HTTP
// error (the `throw_error` below it runs afterwards) but not a `fetch` that
// *rejects* — an offline laptop, a dead DNS lookup, a CORS failure. That left
// `.app-overlay` (`position: fixed; inset: 0; z-index: 10500`) pinned over the
// page with a spinner until the user reloaded.
//
// The `loadingtext !== ''` guard is load-bearing, not defensive: `generate_document`
// holds one overlay open across five nested `getData`/`postData` calls that pass no
// `loadingtext`, so an unconditional teardown here would kill the outer overlay the
// moment the first inner call resolved.

// Function to handle GET requests
export async function getData(
  router: Router,
  endpoint: string,
  loadingtext = "",
) {
  const isAuthenticated = useAuthStore().checkAuth();
  if (!isAuthenticated) {
    ShowLogOutError(router);
    return;
  }

  const url = `${API_URL}/${endpoint}`;

  try {
    if (loadingtext != "") {
      StartLoading(loadingtext);
    }
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: get_token(),
      },
    });
    if (!response.ok) {
      if (response.status == 401) {
        ShowLogOutError(router);
      } else {
        throw_error(response.status);
      }
    }

    return response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Error getting data: ${error.message}`, { cause: error });
    } else {
      throw new Error(`An unknown error occurred.`, { cause: error });
    }
  } finally {
    // See OVERLAY TEARDOWN at the top of this file.
    if (loadingtext !== "") {
      StopLoading();
    }
  }
}

// Function to handle POST requests
export async function postData(
  router: Router,
  endpoint: string,
  data: string,
  loadingtext = "",
) {
  const isAuthenticated = useAuthStore().checkAuth();
  if (!isAuthenticated) {
    ShowLogOutError(router);
    return;
  }

  const url = `${API_URL}/${endpoint}`;

  try {
    if (loadingtext != "") {
      StartLoading(loadingtext);
    }
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: get_token(),
      },
      body: data,
    });
    if (!response.ok) {
      if (response.status == 401) {
        ShowLogOutError(router);
      } else if (response.status !== 409) {
        // 409 conflicts carry a structured JSON body the caller must inspect
        // (e.g. { code: "EMAIL_ALREADY_EXISTS", entity: "..." }). Return the
        // response instead of throwing so callers can surface inline field errors.
        // A 422 problem+json keeps its `errors.<field>` on the thrown error.
        throw_error(
          response.status,
          response.status === 422 ? await readFieldErrors(response) : undefined,
        );
      }
    }

    return response;
  } catch (error) {
    // Already a structured HTTP error from throw_error — rethrow as-is so
    // callers can read `.status` (via getErrorStatus) instead of losing it
    // inside a wrapping Error's `cause`.
    if (error instanceof Error && "status" in error) {
      throw error;
    }
    if (error instanceof Error) {
      throw new Error(`Error posting data: ${error.message}`, { cause: error });
    } else {
      throw new Error(`An unknown error occurred.`, { cause: error });
    }
  } finally {
    // See OVERLAY TEARDOWN at the top of this file.
    if (loadingtext !== "") {
      StopLoading();
    }
  }
}

// Function to handle UPDATE requests
export async function updateData(
  router: Router,
  endpoint: string,
  data: string,
  id: string,
  loadingtext = "",
) {
  const isAuthenticated = useAuthStore().checkAuth();
  if (!isAuthenticated) {
    ShowLogOutError(router);
    return;
  }

  const url = `${API_URL}/${endpoint}/${id}`;

  try {
    if (loadingtext != "") {
      StartLoading(loadingtext);
    }
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: get_token(),
      },
      body: data,
    });
    if (!response.ok) {
      if (response.status == 401) {
        ShowLogOutError(router);
      } else if (response.status !== 409) {
        // 409 conflicts carry a structured JSON body the caller must inspect
        // (e.g. { code: "EMAIL_ALREADY_EXISTS", entity: "..." }). Return the
        // response instead of throwing so callers can surface inline field errors.
        // A 422 problem+json keeps its `errors.<field>` on the thrown error.
        throw_error(
          response.status,
          response.status === 422 ? await readFieldErrors(response) : undefined,
        );
      }
    }
    return response;
  } catch (error) {
    // See postData's identical passthrough — preserves `.status` for callers.
    if (error instanceof Error && "status" in error) {
      throw error;
    }
    if (error instanceof Error) {
      throw new Error(`Error Updating data: ${error.message}`, {
        cause: error,
      });
    } else {
      throw new Error(`An unknown error occurred.`, { cause: error });
    }
  } finally {
    // See OVERLAY TEARDOWN at the top of this file.
    if (loadingtext !== "") {
      StopLoading();
    }
  }
}

// Function to handle DELETE requests
export async function deleteData(
  router: Router,
  endpoint: string,
  id: string,
  loadingtext = "",
) {
  const isAuthenticated = useAuthStore().checkAuth();
  if (!isAuthenticated) {
    ShowLogOutError(router);
    return;
  }

  const url = `${API_URL}/${endpoint}/${id}`;

  try {
    if (loadingtext != "") {
      StartLoading(loadingtext);
    }
    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        Authorization: get_token(),
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      if (response.status == 401) {
        ShowLogOutError(router);
      } else if (
        response.status === 409 ||
        errorText.includes("violates foreign key constraint")
      ) {
        throw new Error(
          "This item is in use and cannot be deleted. Conflict error.",
        );
      } else {
        throw_error(response.status);
      }
    }

    return response;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Error deleting data: ${error.message}`, {
        cause: error,
      });
    } else {
      throw new Error(`An unknown error occurred.`, { cause: error });
    }
  } finally {
    // See OVERLAY TEARDOWN at the top of this file.
    if (loadingtext !== "") {
      StopLoading();
    }
  }
}

// Function to generate documents
export async function generate_document(
  router: Router,
  id: string,
  type: string,
) {
  const isAuthenticated = useAuthStore().checkAuth();
  if (!isAuthenticated) {
    throw_error(401);
    useAuthStore().logout();
    void router.push({ name: "sign-in" });
    return;
  }

  try {
    let en_endpoint: string = "";
    let ar_endpoint: string = "";
    let language: string = "";
    let json_data = "";
    let filename: string = "";

    StartLoading(`Generating ${type} Report...`);

    let result: Record<string, string> = {};

    if (type === "Audit") {
      result = await getData(router, `audit_reports/${id}`);
      language = result["document_language"] ?? "";
      json_data = result["form_data"] ?? "";
      filename = result["name"] ?? "";
      en_endpoint = "audit_reports/gen/audit_en_Report";
      ar_endpoint = "audit_reports/gen/audit_ar_Report";
    } else if (type === "SSB") {
      result = await getData(router, `audit_reports/${id}`);
      language = result["document_language"] ?? "";
      const defaultComplianceText: Record<string, string> = {
        English:
          "Based on our (i) oversight and supervision of the Company’s affairs related to Shari’a compliance, and (ii) a period-specific review of the activities performed, we report that in our opinion, the Company’s financial arrangements, contracts and transactions with its customers, other financial companies and stakeholders for the year ended 17th April 2025  are in compliance with Shari’a standards and guidelines approved by us.",
        العربية:
          "هذا التقرير وما يتضمنه من رأي مستند على إشرافنا على أعمال الشركة المتعلقة بالالتزام بالضوابط والمعايير الشرعية أولًا، ومراجعة عملياتها وأنشطتها للفترة المحددة ثانيًا، وعليه فإننا نؤكد على أن الترتيبات المالية الخاصة بالشركة والعقود والمعاملات المنفذة مع عملائها والأطراف الأخرى التي أُجريت في السنة المالية المنتهية في 31 ديسمبر 2020 قد تمت وفق الضوابط والمعايير الشرعية المعتمدة.",
        Arabic:
          "هذا التقرير وما يتضمنه من رأي مستند على إشرافنا على أعمال الشركة المتعلقة بالالتزام بالضوابط والمعايير الشرعية أولًا، ومراجعة عملياتها وأنشطتها للفترة المحددة ثانيًا، وعليه فإننا نؤكد على أن الترتيبات المالية الخاصة بالشركة والعقود والمعاملات المنفذة مع عملائها والأطراف الأخرى التي أُجريت في السنة المالية المنتهية في 31 ديسمبر 2020 قد تمت وفق الضوابط والمعايير الشرعية المعتمدة.",
      };
      // `?? ""` keeps the old failure: JSON.parse(undefined) threw, and so does "".
      const form_data = JSON.parse(result["form_data"] ?? "");
      json_data = JSON.stringify({
        client_name: form_data["client_name"],
        start_date: form_data["start_date"],
        end_date: form_data["end_date"],
        client_board_name: form_data["client_board_name"],
        scholars: form_data["scholars"],
        // Check if ssb_compliance_data exists and handling default values
        compliance_text:
          form_data["ssb_compliance_data"] &&
          form_data["ssb_compliance_data"]["compliance_text"]
            ? form_data["ssb_compliance_data"]["compliance_text"]
            : defaultComplianceText[language],
        ...(form_data["ssb_compliance_data"] &&
          form_data["ssb_compliance_data"]["additional_opinions"] && {
            additional_opinions:
              form_data["ssb_compliance_data"]["additional_opinions"],
          }),
        ...(form_data["ssb_compliance_data"] &&
          form_data["ssb_compliance_data"]["non_compliance_points"] && {
            non_compliance_points:
              form_data["ssb_compliance_data"]["non_compliance_points"],
          }),
      });

      // A name without "_Audit" used to download as "<name>_SSBundefined".
      const [stem = "", rest = ""] = (result["name"] ?? "").split("_Audit");
      filename = stem + "_SSB" + rest;
      en_endpoint = "audit_reports/gen/ssb_en_Report";
      ar_endpoint = "audit_reports/gen/ssb_ar_Report";
    } else if (type === "Qualified") {
      result = await getData(router, `qualified_reports/${id}`);
      language = result["document_language"] ?? "";
      json_data = result["form_data"] ?? "";
      filename = result["name"] ?? "";
      en_endpoint = "qualified_reports/gen/qualified_en_Report";
      ar_endpoint = "qualified_reports/gen/qualified_ar_Report";
    } else {
      return;
    }
    let response: Response | undefined;
    if (language === "English") {
      response = await postData(router, en_endpoint, json_data);
    } else {
      response = await postData(router, ar_endpoint, json_data);
    }

    if (response === undefined) {
      throw_error(0);
      // `throw_error` always throws, so this is unreachable — but its inferred
      // return type is `void`, not `never`, so the typechecker still needs the
      // `return` to know `response` is defined below.
      return;
    }

    if (!response.ok) {
      throw_error(response.status);
      return;
    }

    const blob = await response.blob();
    downloadBlob(blob, filename);
  } catch (error) {
    log.error("Error downloading file", error);
    throw error;
  } finally {
    // See OVERLAY TEARDOWN at the top of this file. This one already dismissed on
    // success and in the catch, so the rejection window was covered — but the
    // `else { return }` in the type dispatch above sits *after* `StartLoading`,
    // so an unrecognised `type` left the overlay up with no way to clear it. No
    // caller passes one today; a fourth report type would have found it.
    StopLoading();
  }
}

// Builds a descriptive Error for the given HTTP status, with the status
// attached so callers can branch on it (e.g. 403) without parsing the
// message. Toast display is the caller's responsibility — ApiService never
// shows toasts.
function throw_error(status: number, fieldErrors?: Record<string, string>) {
  let errorMessage: string;

  switch (status) {
    case 400:
      errorMessage =
        "Bad request: The server could not understand the request.";
      break;
    case 401:
      errorMessage = "Unauthorized: Session Expired. Login Again!";
      break;
    case 403:
      errorMessage =
        "Forbidden: The server understood the request, but refuses to authorize it.";
      break;
    case 404:
      errorMessage = "Not Found: The requested resource could not be found.";
      break;
    default:
      errorMessage =
        "Network error: An unexpected error occurred on the server.";
      break;
  }

  throw Object.assign(new Error(errorMessage), {
    status,
    ...(fieldErrors && { fieldErrors }),
  });
}

/**
 * Extracts the HTTP status from an error thrown by getData/postData/updateData
 * (attached in throw_error), if present. Lets callers branch on a specific
 * status — e.g. showing a sector-access message on 403 — without a
 * hand-rolled type assertion at every call site.
 */
export function getErrorStatus(error: unknown): number | undefined {
  return error instanceof Error && "status" in error
    ? (error as Error & { status?: number }).status
    : undefined;
}

/**
 * The per-field messages a 422 from getData/postData/updateData carries
 * (attached in throw_error), if present: `{ field: message }`. Safe on
 * anything — null, undefined, a non-object or an error without them gives
 * `undefined` — and it keeps only string messages. Built with
 * `Object.fromEntries`, so a `__proto__` key from the server is an ordinary
 * own property, never the prototype.
 */
export function getFieldErrors(
  error: unknown,
): Record<string, string> | undefined {
  if (typeof error !== "object" || error === null || !("fieldErrors" in error))
    return undefined;
  const { fieldErrors } = error;
  if (
    typeof fieldErrors !== "object" ||
    fieldErrors === null ||
    Array.isArray(fieldErrors)
  )
    return undefined;
  return Object.fromEntries(
    Object.entries(fieldErrors).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string",
    ),
  );
}

function get_token(): string {
  return getStoredAuthToken();
}

/**
 * The backend refused the current token outright (401): it expired, or the
 * user has been deactivated. Retrying cannot succeed; the session is over.
 */
export class SessionRevokedError extends Error {
  constructor() {
    super("Token refresh refused: session revoked");
    this.name = "SessionRevokedError";
  }
}

/**
 * Rotates the backend access token: sends the new MSAL idToken to the backend,
 * which atomically replaces the old token. Updates sessionStorage on success.
 * Returns the new tokenExpiry (ms since epoch). Throws `SessionRevokedError` on
 * a 401, and a plain `Error` on any other failure, which the caller may retry.
 */
export async function refreshBackendToken(
  currentToken: string,
  newToken: string,
): Promise<number> {
  const url = `${API_URL}/users/refresh-token`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: currentToken,
    },
    body: JSON.stringify({ token: newToken }),
  });

  if (response.status === 401) {
    throw new SessionRevokedError();
  }
  if (!response.ok) {
    throw new Error(`Token refresh failed: ${response.status}`);
  }

  const data = await response.json();
  const tokenExpiry = Number(data.token_expiry);
  if (!Number.isFinite(tokenExpiry) || tokenExpiry <= 0) {
    throw new Error("Token refresh: invalid token_expiry in response");
  }

  // Update sessionStorage — re-read to avoid overwriting concurrent writes
  const auth = readAuthData();
  if (auth) {
    writeAuthData({
      ...auth,
      idToken: newToken,
      tokenExpiry,
    });
  }

  return tokenExpiry;
}

export async function login_backend(email: string, token: string) {
  const url = `${API_URL}/users/login`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: get_token(),
      },
      body: JSON.stringify({
        email: email,
        token: token,
      }),
    });

    // if (!response.ok) {
    //   throw_error(response.status);
    // }
    return response;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Error Logging In: ${error.message}`, { cause: error });
    } else {
      throw new Error(`An unknown error occurred.`, { cause: error });
    }
  }
}

/**
 * The signed-in user's own record. Reads `/users/me`, not `/users/{id}` — the
 * latter is admin-only, so every non-admin page load 403'd and silently stopped
 * refreshing role and module grants. The server resolves the id from the token.
 */
export async function get_current_user() {
  const url = `${API_URL}/users/me`;

  try {
    const response = await fetch(url, {
      headers: {
        Authorization: get_token(),
      },
    });

    return response;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Error Fetching User Data: ${error.message}`, {
        cause: error,
      });
    } else {
      throw new Error(`An unknown error occurred.`, { cause: error });
    }
  }
}

// Function to handle DELETE file
export async function deleteFile(
  router: Router,
  endpoint: string,
  loadingtext = "",
) {
  const isAuthenticated = useAuthStore().checkAuth();
  if (!isAuthenticated) {
    ShowLogOutError(router);
    return;
  }

  const url = `${API_URL}/${endpoint}`;

  try {
    if (loadingtext != "") {
      StartLoading(loadingtext);
    }
    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        Authorization: get_token(),
      },
    });

    if (!response.ok) {
      if (response.status == 401) {
        ShowLogOutError(router);
      } else {
        throw_error(response.status);
      }
    }

    return response;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Error deleting data: ${error.message}`, {
        cause: error,
      });
    } else {
      throw new Error(`An unknown error occurred.`, { cause: error });
    }
  } finally {
    // See OVERLAY TEARDOWN at the top of this file.
    if (loadingtext !== "") {
      StopLoading();
    }
  }
}

// Function to handle POST file
export async function PostFile(
  router: Router,
  endpoint: string,
  data: FormData,
  loadingtext = "",
) {
  const isAuthenticated = useAuthStore().checkAuth();
  if (!isAuthenticated) {
    ShowLogOutError(router);
    return;
  }

  const url = `${API_URL}/${endpoint}`;

  try {
    if (loadingtext !== "") {
      StartLoading(loadingtext);
    }

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: get_token(),
      },
      body: data,
    });

    if (!response.ok) {
      if (response.status == 401) {
        ShowLogOutError(router);
      } else {
        throw_error(response.status);
      }
    }

    return response;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Error posting data: ${error.message}`, { cause: error });
    } else {
      throw new Error(`An unknown error occurred.`, { cause: error });
    }
  } finally {
    // See OVERLAY TEARDOWN at the top of this file.
    if (loadingtext !== "") {
      StopLoading();
    }
  }
}

/**
 * A failed HTTP response, carrying the status alongside the message.
 *
 * Thrown so a caller can branch on `status` — a 503 from the certificate render
 * queue is a "try again shortly", a 500 is not — instead of matching the
 * `HTTP error! Status: n` text, which is a message meant for a log. Modelled on
 * `ApiConflictError` in `services/businessAutomationApi.ts`.
 */
export class HttpStatusError extends Error {
  readonly status: number;
  /**
   * A server message meant for the person who made the request, when the
   * server sent one it vouches for. Only ever populated from a 422, whose
   * whole meaning here is "your request is fine, this RECORD cannot be
   * expressed that way" — a condition a member of staff can act on. Every
   * other status keeps its body out of the UI, where it would leak whatever
   * the server happened to say.
   */
  readonly detail?: string;
  constructor(
    status: number,
    message: string,
    options?: ErrorOptions & { detail?: string },
  ) {
    super(message, options);
    this.name = "HttpStatusError";
    this.status = status;
    this.detail = options?.detail;
  }
}

/** Bounds what a 422 body can put on screen. Longer bodies are ignored, not
 * truncated: a message this long is not the curated one-liner this path
 * exists for. */
const MAX_ACTIONABLE_DETAIL_CHARS = 400;

/** A problem+json body's `detail`, or `undefined` when it has none. */
function problemDetail(raw: string): string | undefined {
  const parsed: unknown = JSON.parse(raw);
  if (typeof parsed !== "object" || parsed === null || !("detail" in parsed)) {
    return undefined;
  }
  return typeof parsed.detail === "string" ? parsed.detail.trim() : undefined;
}

/**
 * The actionable message a 422 carries, or `undefined` for every other
 * status. Reading the body is deliberately confined to this one status —
 * see {@link HttpStatusError.detail}.
 */
async function actionableDetail(
  response: Response,
): Promise<string | undefined> {
  if (response.status !== 422) {
    return undefined;
  }
  try {
    const raw = (await response.text()).trim();
    // An RFC 9457 problem carries its operator-facing sentence in `detail`;
    // the server only puts a detail on a problem when it is safe to show.
    // Older routes answer a 422 in prose, read as it stands.
    const isProblem = (response.headers.get("Content-Type") ?? "").startsWith(
      "application/problem+json",
    );
    const body = isProblem ? problemDetail(raw) : raw;
    // Prose only. The one other 422 in this backend answers with a JSON
    // envelope (`avatars::handlers`), and a raw `{"error":...}` in a toast
    // would be both unreadable and a small disclosure; a curated one-liner
    // never starts with a brace or a bracket.
    if (
      !body ||
      body.length > MAX_ACTIONABLE_DETAIL_CHARS ||
      /^[[{]/.test(body)
    ) {
      return undefined;
    }
    return body;
  } catch {
    // A body that cannot be read costs the caller only the generic message.
    return undefined;
  }
}

/**
 * The quoted `filename` of a `Content-Disposition: attachment` header, or
 * `undefined` when there is none to read (absent, unquoted, or hidden from a
 * cross-origin caller by CORS). Only the plain quoted form is read: the routes
 * that opt in send exactly that, with an ASCII name.
 */
export function dispositionFilename(
  disposition: string | null,
): string | undefined {
  const name = /filename="([^"]+)"/i.exec(disposition ?? "")?.[1];
  // A bare file name or nothing: no path separator, no `..`, no control
  // character, nothing a save dialog would read as a location. The routes
  // that opt in send `[A-Za-z0-9#_-]` stems plus an extension.
  return name && SAFE_SAVE_NAME.test(name) && !name.includes("..")
    ? name
    : undefined;
}

const SAFE_SAVE_NAME = /^[A-Za-z0-9][A-Za-z0-9 #_.()-]{0,199}$/;

/**
 * This function downloads a file from the specified endpoint and saves it with the given filename.
 * @param router - Router used to send the user to the login route when the
 * request comes back unauthenticated (`ShowLogOutError`).
 * @param endpoint - Target module endpoint
 * @param filename - The downloaded file will be given this name. Defaults to empty string
 * @param loadingtext - Defaults to empty string.
 * @param preferServerFilename - Save under the `Content-Disposition` name the
 * server sent, keeping `filename` only as the fallback. Opt-in: other routes
 * (e.g. `NamedFile`-served attachments) send on-disk names that must not leak
 * into the save dialog.
 * @returns
 */
export async function getFile(
  router: Router,
  endpoint: string,
  filename = "",
  loadingtext = "",
  preferServerFilename = false,
) {
  const isAuthenticated = useAuthStore().checkAuth();
  if (!isAuthenticated) {
    ShowLogOutError(router);
    return;
  }

  try {
    if (loadingtext !== "") {
      StartLoading(loadingtext);
    }

    const response = await fetch(`${API_URL}/${endpoint}`, {
      method: "GET",
      headers: {
        Authorization: get_token(),
      },
    });

    if (!response.ok) {
      throw new HttpStatusError(
        response.status,
        `HTTP error! Status: ${response.status}`,
        {
          detail: await actionableDetail(response),
        },
      );
    }

    const blob = await response.blob();
    const serverName = preferServerFilename
      ? dispositionFilename(response.headers.get("Content-Disposition"))
      : undefined;
    downloadBlob(blob, serverName ?? filename);
  } catch (error) {
    if (error instanceof Error) {
      // Message unchanged; the status rides along so a caller can branch on it
      // without matching this string. Rewrapping a plain Error as a plain Error
      // keeps every existing caller's `instanceof` check answering as before.
      const message = `Error downloading file ${filename}: ${error.message}`;
      throw error instanceof HttpStatusError
        ? new HttpStatusError(error.status, message, {
            cause: error,
            detail: error.detail,
          })
        : new Error(message, { cause: error });
    } else {
      throw new Error(
        `An unknown error occurred when downlaoding file ${filename}.`,
        {
          cause: error,
        },
      );
    }
  } finally {
    // THE OVERLAY IS DISMISSED ON EVERY EXIT, INCLUDING A THROWN ONE. Until
    // 2026-08-25 the two `StopLoading()` calls sat on the success path only, so
    // a `fetch` that rejected — an offline laptop, a dropped connection, a CORS
    // failure — left `.app-overlay` (`position: fixed; inset: 0`) pinned over
    // the whole page until the user reloaded. `hideOverlay()` is idempotent and
    // the success path already called it unconditionally, so this is the same
    // behaviour everywhere it used to run.
    StopLoading();
  }
}

export async function fetchSignatures(
  router: Router,
  endpoint: string,
  loadingtext = "",
) {
  const isAuthenticated = useAuthStore().checkAuth();
  if (!isAuthenticated) {
    ShowLogOutError(router);
    return;
  }

  const url = `${API_URL}/${endpoint}`;

  try {
    if (loadingtext !== "") {
      StartLoading(loadingtext);
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: get_token(),
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        ShowLogOutError(router);
      } else {
        throw_error(response.status);
      }
    }

    return response.json(); // Return JSON data
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Error fetching signatures: ${error.message}`, {
        cause: error,
      });
    } else {
      throw new Error(`An unknown error occurred.`, { cause: error });
    }
  } finally {
    // See OVERLAY TEARDOWN at the top of this file.
    if (loadingtext !== "") {
      StopLoading();
    }
  }
}

export async function deleteSignatureFile(
  router: Router,
  endpoint: string,
  payload: Record<string, unknown>,
  loadingtext = "",
) {
  const isAuthenticated = useAuthStore().checkAuth();
  if (!isAuthenticated) {
    ShowLogOutError(router);
    return;
  }
  const url = `${API_URL}/${endpoint}`;
  if (loadingtext != "") StartLoading(loadingtext);
  try {
    // `return await`, not `return`: a bare return would hand the promise back and
    // run the `finally` before the request settled, dismissing the overlay early.
    return await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: get_token(),
      },
      body: JSON.stringify(payload),
    });
  } finally {
    // See OVERLAY TEARDOWN at the top of this file. This one had no `try` at all,
    // so a rejected `fetch` left the overlay up on its way out of the function.
    if (loadingtext != "") StopLoading();
  }
}

export async function setDefaultSignature(
  router: Router,
  endpoint: string,
  payload: { mime_type: string; data: string },
) {
  const isAuthenticated = useAuthStore().checkAuth();
  if (!isAuthenticated) {
    ShowLogOutError(router);
    return;
  }
  const url = `${API_URL}/${endpoint}`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: get_token(),
    },
    body: JSON.stringify(payload),
  });
  return response;
}

export async function fetchSignerSuggestions(
  router: Router,
  userId: number,
  loadingtext = "",
) {
  const isAuthenticated = useAuthStore().checkAuth();
  if (!isAuthenticated) {
    ShowLogOutError(router);
    return;
  }

  const endpoint = `signature_platform/signer_suggestions/${userId}`;
  const url = `${API_URL}/${endpoint}`;

  try {
    if (loadingtext !== "") {
      StartLoading(loadingtext);
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: get_token(),
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        ShowLogOutError(router);
      } else {
        throw_error(response.status);
      }
    }

    return response.json();
  } catch (error) {
    if (error instanceof Error) {
      // `log`, not `console` (see srb_dev_vue/CLAUDE.md), and the error object
      // rather than `error.message`: the second argument keeps the stack and the
      // `cause` chain, which the interpolated string discarded. The thrown
      // messages below are unchanged — callers match on them.
      log.error("Error fetching signer suggestions", error);
      throw new Error(`Error fetching signer suggestions: ${error.message}`, {
        cause: error,
      });
    } else {
      log.error("An unknown error occurred fetching signer suggestions", error);
      throw new Error(`An unknown error occurred.`, { cause: error });
    }
  } finally {
    // See OVERLAY TEARDOWN at the top of this file.
    if (loadingtext !== "") {
      StopLoading();
    }
  }
}

export async function fetchScholarFullName(email: string) {
  const url = `${API_URL}/signature_platform/scholar_full_name/${encodeURIComponent(email)}`;
  const response = await fetch(url, { method: "GET" });
  if (!response.ok) return null;
  const data = await response.json();
  return data.full_name || null;
}

export async function fetchScholarsWithSignatures(router: Router) {
  return await getData(router, "signature_platform/scholars_with_signatures");
}

export async function fetchLatestSignature(router: Router, userId: number) {
  return await getData(
    router,
    `signature_platform/assets/signatures/user/${userId}/latest`,
  );
}
export async function fetchLatestInitial(router: Router, userId: number) {
  return await getData(
    router,
    `signature_platform/assets/initials/user/${userId}/latest`,
  );
}

export interface PersonRef {
  favorite_id: number;
  kind: "user" | "scholar" | "external_contact";
  id: number;
  name: string;
  email: string;
  company: string | null;
  title: string | null;
  avatar_url: string | null;
  is_stale: boolean;
}

export async function fetchFavorites(router: Router): Promise<PersonRef[]> {
  if (!useAuthStore().checkAuth()) {
    ShowLogOutError(router);
    return [];
  }
  const url = `${API_URL}/signature_platform/favorites`;
  const r = await fetch(url, { headers: { Authorization: get_token() } });
  if (!r.ok) {
    log.error("fetchFavorites failed", r.status);
    return [];
  }
  const data = await r.json();
  return (data.favorites ?? []) as PersonRef[];
}

export async function addFavoriteById(
  router: Router,
  kind: PersonRef["kind"],
  id: number,
): Promise<PersonRef | null> {
  if (!useAuthStore().checkAuth()) {
    ShowLogOutError(router);
    return null;
  }
  const url = `${API_URL}/signature_platform/favorites`;
  const r = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: get_token(),
    },
    body: JSON.stringify({ kind, id }),
  });
  if (!r.ok) {
    log.error("addFavoriteById failed", r.status);
    return null;
  }
  const data = await r.json();
  return data.favorite as PersonRef;
}

export async function addFavoriteByEmail(
  router: Router,
  payload: {
    name: string;
    email: string;
    company?: string | null;
    title?: string | null;
  },
): Promise<PersonRef | null> {
  if (!useAuthStore().checkAuth()) {
    ShowLogOutError(router);
    return null;
  }
  const url = `${API_URL}/signature_platform/favorites`;
  const r = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: get_token(),
    },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    log.error("addFavoriteByEmail failed", r.status);
    return null;
  }
  const data = await r.json();
  return data.favorite as PersonRef;
}

export async function deleteFavorite(
  router: Router,
  favoriteId: number,
): Promise<boolean> {
  if (!useAuthStore().checkAuth()) {
    ShowLogOutError(router);
    return false;
  }
  const url = `${API_URL}/signature_platform/favorites/${favoriteId}`;
  const r = await fetch(url, {
    method: "DELETE",
    headers: { Authorization: get_token() },
  });
  return r.ok;
}

export async function reorderFavorites(
  router: Router,
  orderedFavoriteIds: number[],
): Promise<boolean> {
  if (!useAuthStore().checkAuth()) {
    ShowLogOutError(router);
    return false;
  }
  const url = `${API_URL}/signature_platform/favorites/reorder`;
  try {
    const r = await fetch(url, {
      method: "PATCH",
      headers: {
        Authorization: get_token(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ordered_favorite_ids: orderedFavoriteIds }),
    });
    if (!r.ok) log.error("reorderFavorites non-ok", r.status);
    return r.ok;
  } catch (err) {
    // Network failure, abort, etc. — treat as a non-ok response so the
    // caller can roll back without an unhandled rejection.
    log.error("reorderFavorites network error", err);
    return false;
  }
}
